import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { listStandardAgreements, StandardAgreement } from "../../services/StandardAgreements";
import { isPreparing } from "./compare-utils";

const POLL_MS = 3000;

/**
 * The organization's standard agreements, kept fresh while any is still being
 * prepared: polls every 3 s, but only while the page is visible, and stops
 * when nothing is preparing. `null` until the first load.
 */
export const useStandards = () => {
  const { t } = useI18n();
  const failedText = t.comparisons.standardsFailed;
  const [standards, setStandards] = useState<StandardAgreement[] | null>(null);

  const reload = useCallback(
    async (quiet = false) => {
      try {
        const res = await listStandardAgreements();
        if (res.success && res.data) setStandards(res.data);
        else {
          setStandards((prev) => prev ?? []);
          if (!quiet) toast.error(failedText);
        }
      } catch {
        setStandards((prev) => prev ?? []);
      }
    },
    [failedText],
  );

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preparing = !!standards?.some(isPreparing);
  useEffect(() => {
    if (!preparing) return;
    const visible = () => document.visibilityState === "visible";
    const timer = setInterval(() => visible() && void reload(true), POLL_MS);
    const onVisible = () => visible() && void reload(true);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [preparing, reload]);

  return { standards, reload, preparing };
};
